import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';

const STATUS_STYLES = {
  en_route:  { border: '#16a34a', bg: '#dcfce7', color: '#16a34a', label: 'En Route' },
  window_open: { border: '#d97706', bg: '#fef3c7', color: '#d97706', label: 'Window Open' },
  handoff:   { border: '#9333ea', bg: '#f3e8ff', color: '#9333ea', label: 'Handoff' },
  scheduled: { border: '#94a3b8', bg: '#f1f5f9', color: '#64748b', label: 'Scheduled' },
  confirmed: { border: '#16a34a', bg: '#dcfce7', color: '#16a34a', label: 'Confirmed' },
  pending:   { border: '#d97706', bg: '#fef3c7', color: '#d97706', label: 'Pending' },
};

function getStatusStyle(status) {
  const key = (status || '').toLowerCase().replace(/[\s-]/g, '_');
  return STATUS_STYLES[key] || STATUS_STYLES.scheduled;
}

function VIPCalendarContent() {
  const { t } = useTranslation();
  const { T, S } = useTheme();
  const [bookings, setBookings] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [shopFilter, setShopFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookData, shopData] = await Promise.all([
        api.getAdminPremiumBookings(shopFilter || null, statusFilter || null),
        api.getAdminShops(),
      ]);
      setBookings(Array.isArray(bookData) ? bookData : []);
      setShops(shopData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [shopFilter, statusFilter]);

  const shopById = (id) => shops.find(s => s.id === id);

  // Filter bookings by selected date
  const dayBookings = bookings.filter(b => {
    const bDate = b.booking_date || (b.created_at && b.created_at.split('T')[0]);
    return bDate === selectedDate;
  });

  // KPI counts
  const enRouteCount = dayBookings.filter(b => (b.status || '').toLowerCase().includes('en_route') || (b.status || '').toLowerCase().includes('route')).length;
  const windowOpenCount = dayBookings.filter(b => (b.status || '').toLowerCase().includes('window')).length;
  const handoffCount = dayBookings.filter(b => (b.status || '').toLowerCase().includes('handoff')).length;
  const totalToday = dayBookings.length;

  const kpis = [
    { label: t('admin.enRouteNow', 'En Route Now'), value: enRouteCount, color: '#16a34a', bg: '#dcfce7' },
    { label: t('admin.windowsOpen', 'Windows Open'), value: windowOpenCount, color: '#d97706', bg: '#fef3c7' },
    { label: t('admin.handoffsActive', 'Handoffs Active'), value: handoffCount, color: '#9333ea', bg: '#f3e8ff' },
    { label: t('admin.todaysWindows', "Today's Windows"), value: totalToday, color: T.orange, bg: T.bgCard },
  ];

  return (
    <AdminLayout role="admin">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={S.pageTitle}>{t('admin.vipCalendar', 'VIP Calendar - Live Tracking')}</h1>
        <div style={{ width: '40px', height: '2px', background: T.orange, marginBottom: '1.5rem' }} />

        {/* Filters */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem', alignItems: 'center' }}>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            style={{ ...S.input, width: '180px', marginBottom: 0 }}
          />

          <select
            value={shopFilter}
            onChange={e => setShopFilter(e.target.value)}
            style={{ ...S.select, width: '200px', fontSize: '0.82rem', marginBottom: 0 }}
          >
            <option value="">{t('admin.allSalons', 'All Salons')}</option>
            {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ ...S.select, width: '200px', fontSize: '0.82rem', marginBottom: 0 }}
          >
            <option value="">{t('admin.allStatuses', 'All Statuses')}</option>
            <option value="en_route">{t('admin.enRoute', 'En Route')}</option>
            <option value="window_open">{t('admin.windowOpen', 'Window Open')}</option>
            <option value="handoff">{t('admin.handoff', 'Handoff')}</option>
            <option value="scheduled">{t('admin.scheduled', 'Scheduled')}</option>
          </select>

          {(shopFilter || statusFilter || selectedDate !== new Date().toISOString().split('T')[0]) && (
            <button
              onClick={() => { setShopFilter(''); setStatusFilter(''); setSelectedDate(new Date().toISOString().split('T')[0]); }}
              style={{ ...S.btnOutline, padding: '0.4rem 0.8rem', fontSize: '0.75rem', height: '36px' }}
            >
              {t('admin.resetToToday', 'Reset to Today')}
            </button>
          )}
        </div>

        {/* Live KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {kpis.map((kpi, i) => (
            <div key={i} style={{ ...S.statCard, borderLeft: `4px solid ${kpi.color}` }}>
              <span style={{ ...S.statVal, color: kpi.color }}>{kpi.value}</span>
              <span style={S.statLabel}>{kpi.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Booking Cards Grid */}
      {loading ? <p style={{ color: T.muted }}>{t('common.loading', 'Loading...')}</p> : dayBookings.length === 0 ? (
        <p style={{ color: T.muted, fontStyle: 'italic' }}>{t('admin.noVIPBookings', 'No VIP bookings for this date.')}</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {dayBookings.map(b => {
            const ss = getStatusStyle(b.status);
            const shop = shopById(b.shop_id);
            return (
              <div key={b.id} style={{
                ...S.card,
                borderLeft: `4px solid ${ss.border}`,
                padding: '1.25rem',
              }}>
                {/* Status Badge + Time */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
                  <span style={{
                    padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.72rem',
                    fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase',
                    background: ss.bg, color: ss.color,
                  }}>
                    {b.status || ss.label}
                  </span>
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: T.white, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.04em' }}>
                    {b.booking_time || b.start_time || '—'}
                    {(b.end_time) && ` - ${b.end_time}`}
                  </span>
                </div>

                {/* Client */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: ss.border, color: '#fff', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 700,
                  }}>
                    {(b.user_name || b.client_name || b.user?.name || '?')[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: T.white, fontSize: '0.9rem' }}>
                      {b.user_name || b.client_name || b.user?.name || `Client #${b.user_id}`}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: T.muted }}>
                      {t('admin.vipClient', 'VIP Client')}
                    </div>
                  </div>
                </div>

                {/* Artist */}
                {(b.artist_name || b.worker_name || b.service_name) && (
                  <div style={{ fontSize: '0.82rem', color: T.text, marginBottom: '0.4rem' }}>
                    <span style={{ color: T.muted }}>{t('admin.artist', 'Artist')}: </span>
                    <span style={{ fontWeight: 600 }}>{b.artist_name || b.worker_name || '—'}</span>
                    {b.service_name && (
                      <span style={{ color: T.muted }}> &middot; {b.service_name}</span>
                    )}
                  </div>
                )}

                {/* Salon + Branch */}
                <div style={{ fontSize: '0.78rem', color: T.muted, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <span style={{ color: T.orange, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.05em' }}>
                    {shop?.name || `Shop #${b.shop_id}`}
                  </span>
                  {b.branch_name && (
                    <>
                      <span>&rsaquo;</span>
                      <span>{b.branch_name}</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}

export default function VIPCalendar() {
  return <ProtectedRoute requireSuperAdmin><VIPCalendarContent /></ProtectedRoute>;
}
